/**
 * Auth token storage.
 *
 * The token each tab uses for requests is kept in sessionStorage, so
 * tabs viewing different workspaces hold their own (scoped) token and can't
 * overwrite each other. A copy is mirrored into **localStorage** as a "seed":
 * it's read to bootstrap a fresh tab, and to recover a tab whose own token
 * expired while another tab kept the session alive.
 */

const ID_TOKEN_KEY = "id_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const LAST_SCOPE_KEY = "last_session_scope";

export type SessionScope = {
  orgId: string;
  wsId?: string;
};

// Last scoped (org, ws); localStorage so new tabs start scoped.
export const getLastSessionScope = (): SessionScope | null => {
  const raw = localStorage.getItem(LAST_SCOPE_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      typeof (parsed as SessionScope).orgId === "string"
    ) {
      return parsed as SessionScope;
    }
    return null;
  } catch {
    return null;
  }
};

export const setLastSessionScope = (scope: SessionScope): void => {
  localStorage.setItem(LAST_SCOPE_KEY, JSON.stringify(scope));
};

export const clearLastSessionScope = (): void => {
  localStorage.removeItem(LAST_SCOPE_KEY);
};

// Snapshot the shared localStorage seed into sessionStorage, once
// per tab, so this tab reads its own isolated and internally-consistent
// id/refresh tokens.
const seedSessionFromLocalStorage = (): void => {
  const alreadySeeded =
    sessionStorage.getItem(ID_TOKEN_KEY) !== null ||
    sessionStorage.getItem(REFRESH_TOKEN_KEY) !== null;
  if (alreadySeeded) {
    return;
  }
  const idSeed = localStorage.getItem(ID_TOKEN_KEY);
  const refreshSeed = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (idSeed !== null) {
    sessionStorage.setItem(ID_TOKEN_KEY, idSeed);
  }
  if (refreshSeed !== null) {
    sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshSeed);
  }
};

const readToken = (key: string): string | null => {
  seedSessionFromLocalStorage();
  return sessionStorage.getItem(key);
};

export const getIdToken = (): string | null => readToken(ID_TOKEN_KEY);

export const getRefreshToken = (): string | null =>
  readToken(REFRESH_TOKEN_KEY);

export const setTokens = (idToken: string, refreshToken: string): void => {
  // per-tab token
  sessionStorage.setItem(ID_TOKEN_KEY, idToken);
  sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  // seed for future tabs
  localStorage.setItem(ID_TOKEN_KEY, idToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

// Removing the seed is how a logout reaches the other tabs (see
// onCrossTabLogout), so only end a session here that is over everywhere.
export const clearTokens = (): void => {
  sessionStorage.removeItem(ID_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(ID_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  // the next sign-in may be a different user
  clearLastSessionScope();
};

export const hasSharedSession = (): boolean =>
  localStorage.getItem(REFRESH_TOKEN_KEY) !== null;

/**
 * Re-snapshot the shared seed over this tab's tokens, for when this tab's own
 * token expired while another tab kept the session alive and mirrored a newer
 * one into the seed. Returns false when there is nothing newer to adopt, i.e.
 * the session really is over.
 */
export const adoptSharedSession = (): boolean => {
  const idSeed = localStorage.getItem(ID_TOKEN_KEY);
  const refreshSeed = localStorage.getItem(REFRESH_TOKEN_KEY);
  const isNewerThanThisTab =
    idSeed !== null &&
    refreshSeed !== null &&
    refreshSeed !== sessionStorage.getItem(REFRESH_TOKEN_KEY);
  if (!isNewerThanThisTab) {
    return false;
  }
  sessionStorage.setItem(ID_TOKEN_KEY, idSeed);
  sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshSeed);
  return true;
};

export const onCrossTabLogout = (onLogout: () => void): (() => void) => {
  const handler = (event: StorageEvent) => {
    // clearTokens() removes both keys (two storage events); react only to the
    // refresh token's removal so the callback fires once, not twice.
    const loggedOut =
      event.storageArea === localStorage &&
      event.key === REFRESH_TOKEN_KEY &&
      event.newValue === null;

    if (loggedOut) {
      onLogout();
    }
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
};
